// Small pieces shared by the signed-out screens.
import { AuthApiError, isAuthApiError, SessionPayload } from '../../auth/authApi';
import { clearSession, storeSession } from '../../auth/session';
import { isRegion, Region } from '../../regionBootstrap';

/** `&region=eu` in an e-mailed link: the regional API that holds the row the link points at. */
export const regionFrom = (params: URLSearchParams): Region | null => {
  const value = (params.get('region') || '').trim().toLowerCase();
  return isRegion(value) ? value : null;
};

export const isEmail = (value: string): boolean => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value.trim());

/** What to show for a failed request: the API's sentence, or a plain fallback for the unexpected. */
export const messageOf = (err: unknown): string =>
  isAuthApiError(err) ? err.message : 'Something went wrong on our side. Nothing was changed — try again in a moment.';

export const codeOf = (err: unknown): string => (isAuthApiError(err) ? (err as AuthApiError).code : 'unexpected');

/**
 * A session was opened: store it and start the app the same way it starts after any sign-in — a
 * fresh load at the app's base, where the usual bootstrap (role, profile, onboarding) takes over.
 * Whatever a previous person left in this browser (the app's stored state, ids, role) goes first.
 * `region`: the regional API that opened the session.
 */
export function enterApp(session: SessionPayload, region?: Region | null): void {
  clearSession();
  storeSession(session, region);
  window.location.replace(`${window.location.origin}${window.__APP_BASE__ || ''}/`);
}

// A provider sign-in that was refused returns as `?sso_error=<code>` — the API's sentence does not
// travel with it, so these codes are worded here (kept in step with the API's messages).
const SSO_ERRORS = new Map<string, string>([
  ['sso_cancelled', "The sign-in was cancelled before it finished. Try again when you're ready."],
  ['sso_expired', 'That sign-in took too long or was already used. Start again.'],
  ['sso_not_available', "That sign-in option isn't available here."],
  [
    'sso_address_not_allowed',
    "That sign-in option is only for its organization's own email addresses. Use the option that matches your address."
  ],
  [
    'provider_not_allowed',
    "This email signs in through its organization's own sign-in option or with a password. Use one of those instead."
  ],
  [
    'account_not_provisioned',
    "This account isn't set up here yet. If you were invited, open the link in your invitation email — otherwise ask your organization admin for an invitation."
  ],
  [
    'invitation_password_only',
    'This invitation can only be accepted with an email and a password. Choose a password below.'
  ],
  ['account_disabled', 'This account has been deactivated. Contact your organization admin.'],
  ['different_sign_in_method', 'This email signs in a different way. Use the option you used before.'],
  [
    'link_required',
    'This email already signs in with a password. Sign in with it, then connect the provider under Password and security.'
  ],
  ['reauth_required', "Confirm it's you before changing how you sign in."],
  ['reauth_mismatch', "That sign-in belongs to a different account. Confirm with the account you're signed in to."],
  ['identity_in_use', 'That account is already connected to another Dosen account.'],
  ['identity_already_linked', 'That account is already connected to this account.'],
  [
    'identity_not_linkable',
    "That account can't be connected right now. If it belongs to another account, disconnect it there first."
  ],
  ['method_is_main', "This is the account's main sign-in method. It can't be removed."],
  ['method_is_password', "Password sign-in can't be removed from an account."],
  ['last_method', 'This is the only way to sign in to this account. Set a password or connect another method first.'],
  ['password_already_set', 'This account already has a password. Change it with your current password.'],
  [
    'existing_account_password_required',
    'You already have a Dosen account with this email. Enter its password, or reset it first.'
  ],
  [
    'invitation_email_mismatch',
    'This invitation was sent to a different address. Sign in with the invited address to accept it.'
  ],
  ['invitation_already_accepted', 'This invitation was already accepted. Sign in to continue.'],
  ['invitation_expired', 'This invitation has expired. Ask your admin to send a new one.'],
  ['invitation_revoked', 'This invitation was withdrawn. Ask your admin to send a new one.'],
  ['invitation_invalid', "This invitation link isn't valid. Ask your admin to send a new one."],
  [
    'account_exists',
    "This email already has a Dosen account that can't be added to this organization. Contact support@dosen.io."
  ],
  ['acceptance_in_progress', "We're still setting up your account. Try again in a few seconds."],
  ['too_many_attempts', 'Too many attempts. Wait a few minutes and try again.'],
  [
    'identity_provider_error',
    'Something went wrong on our side and nothing was changed. If it keeps happening, write to support@dosen.io.'
  ]
]);

export const ssoErrorMessage = (code: string | null): string | null =>
  code
    ? SSO_ERRORS.get(code) ?? "We couldn't reach the sign-in service. Nothing was changed — try again in a moment."
    : null;

// A provider sign-in refused with `link_required`: the API keeps the provider identity for a few
// minutes under a hand-off code; the sign-in screen asks for the account's password to connect it.
// The code stays in this tab's storage, never in the address bar.
const LINK_REQUIRED_KEY = 'dosen.auth.linkRequired';

export interface LinkRequired {
  handoff: string;
  /** how the account signs in, e.g. "with a password" */
  method: string;
}

export function rememberLinkRequired(pending: LinkRequired): void {
  try {
    sessionStorage.setItem(LINK_REQUIRED_KEY, JSON.stringify(pending));
  } catch {
    /* storage unavailable */
  }
}

export function readLinkRequired(): LinkRequired | null {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(LINK_REQUIRED_KEY) || 'null');
    return parsed && typeof parsed.handoff === 'string' ? parsed : null;
  } catch {
    return null;
  }
}

export function forgetLinkRequired(): void {
  try {
    sessionStorage.removeItem(LINK_REQUIRED_KEY);
  } catch {
    /* storage unavailable */
  }
}
